// src/components/Invitation/InviteCollaborator.js
import React, { useState, useEffect } from 'react';
import { useUserContext } from '../../contexts/UserContext';
import { auth, db } from '../../firebase/config'; 
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import './invitation.css';

function InviteCollaborator({ profileId, onInviteSent }) {
  const [inviteLink, setInviteLink] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const { user, businessProfiles } = useUserContext();

  // Get API URL from environment or use default
  const API_URL = process.env.REACT_APP_API_URL || 'https://southamerica-east1-drecenna.cloudfunctions.net/api';

  // State to track if user can invite
  const [canInvite, setCanInvite] = useState(false);
  const [ownershipChecked, setOwnershipChecked] = useState(false);
  
  // Get the active profile ID
  const getActiveProfileId = () => {
    if (profileId) return profileId;
    return localStorage.getItem('active_business_profile_id');
  };
  
  // Check if the user has permission to invite collaborators
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        setError('');
        const activeProfileId = getActiveProfileId();
        
        if (!activeProfileId) {
          setError('No active profile selected');
          setCanInvite(false);
          setOwnershipChecked(true);
          return;
        }
        
        if (!user?.uid) {
          setError('User not authenticated');
          setCanInvite(false);
          setOwnershipChecked(true);
          return;
        }
        
        console.log('Checking permissions for profile:', activeProfileId);

        // First try to find the profile in context
        let profileData = null;
        let profileFound = false;
        
        if (businessProfiles?.length > 0) {
          const profile = businessProfiles.find(p => p.id === activeProfileId);
          if (profile) {
            profileData = profile;
            profileFound = true;
          }
        }
        
        // If not found in context, fetch directly from Firestore
        if (!profileFound) {
          console.log('Profile not found in context, fetching from Firestore');
          const profileRef = doc(db, "users", user.uid, "businessProfiles", activeProfileId);
          const profileDoc = await getDoc(profileRef);
          
          if (profileDoc.exists()) {
            profileData = profileDoc.data();
            profileData.id = profileDoc.id;
            profileFound = true;
          }
        }
        
        if (!profileFound) {
          setError('Profile not found');
          setCanInvite(false);
          setOwnershipChecked(true);
          return;
        }
        
        // Now check ownership with improved logging
        const isCreator = profileData.userId === user.uid;
        const isMarkedAsOwner = 
          profileData.isDefault === true || 
          (profileData.members && profileData.members[user.uid] === 'owner');
          
        const isOwner = isCreator || isMarkedAsOwner;
        
        // Log detailed ownership information
        console.log('Ownership verification:', {
          profileId: activeProfileId,
          profileName: profileData.profileName || 'Unknown',
          userId: user.uid,
          creatorId: profileData.userId || 'Not set',
          isCreator,
          isDefault: profileData.isDefault === true,
          isMarkedAsOwner,
          role: profileData.members?.[user.uid] || 'Unknown',
          membersExists: !!profileData.members,
          finalIsOwner: isOwner
        });
        
        // NEW: If members field is missing but user is creator, fix it
        if (!profileData.members && isCreator) {
          try {
            console.log('Fixing missing members field in profile');
            const profileRef = doc(db, "users", user.uid, "businessProfiles", activeProfileId);
            await updateDoc(profileRef, {
              members: { [user.uid]: 'owner' }
            });
            console.log('Successfully updated profile with members field');
          } catch (updateError) {
            console.error('Error updating profile with members field:', updateError);
          }
        }
        
        setCanInvite(isOwner);
        setOwnershipChecked(true);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setError(`Permission check failed: ${error.message}`);
        setCanInvite(false);
        setOwnershipChecked(true);
      }
    };
    
    checkPermissions();
  }, [profileId, user, businessProfiles]);

  // Function to get auth headers with better error handling
  const getAuthHeaders = async () => {
    try {
      if (!auth.currentUser) {
        throw new Error('No authenticated user');
      }
      
      // Force refresh the token to ensure it's valid
      const token = await auth.currentUser.getIdToken(true);
      
      // Log token details for debugging (partial token only for security)
      const tokenPreview = token.substring(0, 10) + '...' + token.substring(token.length - 5);
      console.log(`Got auth token: ${tokenPreview}, length: ${token.length}`);
      
      return {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
    } catch (error) {
      console.error('Failed to get auth token:', error);
      throw new Error(`Authentication error: ${error.message}`);
    }
  };

  // Function to test API connection
  const testAPIConnection = async () => {
    try {
      setConnectionStatus('testing');
      
      // Test the API with fetch
      const response = await fetch(`${API_URL}/health`);
      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
      
      const data = await response.json();
      console.log('API connection test successful:', data);
      setConnectionStatus('connected');
      return true;
    } catch (error) {
      console.error('API connection test failed:', error);
      setConnectionStatus('unreachable');
      return false;
    }
  };

  // Function to generate the invitation link
  const handleGenerateInvite = async () => {
    try {
      setIsLoading(true);
      setError('');
      setCopied(false);
      setSuccess(false);
      
      // Get the active profile ID
      const activeProfileId = getActiveProfileId();
      
      // Safety checks
      if (!activeProfileId) {
        throw new Error('No active profile selected');
      }
      
      if (!canInvite) {
        throw new Error('You do not have permission to invite collaborators to this workspace');
      }
      
      // Test API connection first
      const connectionOk = await testAPIConnection();
      if (!connectionOk) {
        throw new Error('Cannot connect to invitation service. Please try again later.');
      }
      
      // Get fresh auth headers
      const authConfig = await getAuthHeaders();
      
      // Prepare the request payload
      const requestPayload = {
        profileId: activeProfileId,
        timestamp: new Date().toISOString(),
        clientInfo: {
          userAgent: navigator.userAgent,
          language: navigator.language
        }
      };
      
      console.log('Sending invitation request:', requestPayload);
      
      // Send the request with fetch API
      try {
        const response = await fetch(`${API_URL}/generate-invitation`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authConfig.headers.Authorization
          },
          body: JSON.stringify(requestPayload)
        });
        
        // Handle HTTP errors
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          
          // Detailed error handling based on status code
          if (response.status === 403) {
            throw new Error('Permission denied: You do not have the required permissions to create invitations.');
          } else if (response.status === 401) {
            throw new Error('Authentication failed: Please try logging out and back in.');
          } else if (errorData.error) {
            throw new Error(`Server error: ${errorData.error}`);
          } else {
            throw new Error(`Request failed with status: ${response.status}`);
          }
        }
        
        // Parse the response
        const data = await response.json();
        console.log('Invitation response:', data);
        
        if (data.success && data.invitationLink) {
          setInviteLink(data.invitationLink);
          setSuccess(true);
          
          if (onInviteSent) {
            onInviteSent(data.invitationLink);
          }
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (apiError) {
        // Enhanced error handling
        console.error('API error details:', apiError);
        throw apiError;
      }
    } catch (error) {
      console.error('Error generating invitation link:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to copy the invitation link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch(err => {
        console.error('Could not copy text: ', err);
        setError('Failed to copy link to clipboard. Please try selecting and copying the link manually.');
      });
  };

  // If we're still checking permissions, show loading
  if (!ownershipChecked) {
    return (
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-center space-x-2 py-4">
          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600"></div>
          <p className="text-blue-600 font-medium">Checking permissions...</p>
        </div>
      </div>
    );
  }

  // If user doesn't have permission, show appropriate message
  if (!canInvite && !isLoading && !error) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm font-medium text-yellow-800">
              Permission Required
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              Only workspace owners can invite collaborators.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {!inviteLink ? (
        <div className="text-center">
          <button
            onClick={handleGenerateInvite}
            disabled={isLoading || !canInvite}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors duration-200 shadow-sm hover:shadow-md disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Generating Invitation...
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                Generate Invitation Link
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Success message */}
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="h-5 w-5 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm font-medium text-green-800">
                Invitation link generated successfully!
              </p>
            </div>
          </div>

          {/* Link display and copy */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Invitation Link
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={inviteLink}
                readOnly
                className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-md text-sm font-mono text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={handleCopyLink}
                className={`inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                  copied 
                    ? 'bg-green-100 text-green-700 border border-green-300' 
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {copied ? (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Copied!
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Actions and info */}
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => {
                setInviteLink('');
                setSuccess(false);
                setCopied(false);
              }}
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Generate New Link
            </button>
            
            <div className="flex items-center text-xs text-gray-500">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Expires in 24 hours
            </div>
          </div>
        </div>
      )}
      
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-red-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-medium text-red-800">Error generating invitation</h4>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              
              {connectionStatus === 'unreachable' && (
                <p className="text-sm text-red-700 mt-2">
                  Could not connect to the server. Please check your internet connection.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InviteCollaborator;